import { useMemo } from 'react';

import {
  FREE_ANALYTICS_SKU,
  FREE_FEEDBACK_SKU,
} from '@hotjar/webapp/domains/common/product/constants';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import type { Product } from '@hotjar/webapp/domains/common/product/types';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import {
  assertIsFeedbackProduct,
  extractSkuFromPlanId,
  getAnalyticsProductBySku,
  getBundledPlanId,
  getDefaultProductByTier,
  getProductBySku,
  isBundledPlanId,
} from '@hotjar/webapp/domains/common/product/utils';
import type { Site } from '@hotjar/site';
import { useAccountTrialInfo, TrialState } from '@hotjar/account';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { useGetSiteEffectiveProducts } from '@hotjar/webapp/domains/common/product/hooks/useGetSiteProducts';

import { BUSINESS } from '../../plan/feature-group';
import { TrafficCoverageLevel } from '../../sampling/types';

interface UsePlanRecommendations {
  isLoading: boolean;
  legacyPlanId: string | null;
  products: Record<ProductType, Product | null>;
}

const getInitialRecommendedPlanId = (
  initialRecommendedPlanId: string,
  productType: ProductType,
) => {
  return initialRecommendedPlanId && isBundledPlanId(initialRecommendedPlanId)
    ? extractSkuFromPlanId(initialRecommendedPlanId, productType)
    : initialRecommendedPlanId;
};

/**
 * Returns the recommended ask and observe products or no recommendation for a site based on a decision tree.
 * See: https://hotjar.atlassian.net/browse/BUXP-525
 */
export const usePlanRecommendations = (
  site: Site | undefined,
  initialRecommendedPlanId?: string | null,
): UsePlanRecommendations => {
  const { data: productCatalog, isLoading: isLoadingProductCatalog } = useProductCatalog();
  const {
    days,
    dailyTraffic,
    isLoading: isLoadingTrafficCoverage,
    coverageLevel,
  } = useTrafficCoverage(site);
  const trialInfo = useAccountTrialInfo(site);
  const {
    effectiveAnalyticsProduct: effectiveObserveProduct,
    isLoading: isLoadingEffectiveProducts,
  } = useGetSiteEffectiveProducts(site);
  const defaultObserveProduct = getDefaultProductByTier(
    BUSINESS,
    ProductType.Analytics,
    productCatalog,
  );
  const effectiveObserveProductIndex =
    productCatalog?.analyticsProducts.findIndex(
      (product) => product.sku === effectiveObserveProduct?.sku,
    ) ?? -1;
  const defaultObserveProductIndex =
    productCatalog?.analyticsProducts.findIndex(
      (product) => product.sku === defaultObserveProduct?.sku,
    ) ?? -1;

  const isLoading =
    !site || isLoadingProductCatalog || isLoadingTrafficCoverage || isLoadingEffectiveProducts;

  /**
   * We want sites to have 100% coverage, so we need to find the lowest plan that gives them 100% coverage,
   * that is, the plan that offers at least as much daily CC sessions as the site's actual daily session count.
   *
   * If we have 0 days of traffic data for the site, we fallback to the lowest Business product.
   */
  const analyticsProduct = useMemo(() => {
    if (isLoading || !productCatalog) {
      return null;
    }

    if (initialRecommendedPlanId) {
      const basedOnInitialRecommendedPlanId = getInitialRecommendedPlanId(
        initialRecommendedPlanId,
        ProductType.Analytics,
      );
      return getProductBySku(basedOnInitialRecommendedPlanId, productCatalog);
    }

    /**
     * Default to recommending Observe Business 500 if we have 0 days of traffic, and the site's
     * current plan is lower than that default plan. Otherwise, if 0 days of traffic, but the
     * site's plan is already higher than the default, recommend the currently effective plan.
     */
    if (days === 0) {
      if (defaultObserveProductIndex > effectiveObserveProductIndex) {
        return getDefaultProductByTier(BUSINESS, ProductType.Analytics, productCatalog);
      }

      return effectiveObserveProduct ?? null;
    }

    const paidSelfServableProducts = productCatalog?.analyticsProducts.filter(
      (product) => product.sku !== FREE_ANALYTICS_SKU && product.selfServeEnabled,
    );
    const lowestProduct = paidSelfServableProducts?.[0];
    const highestProduct = paidSelfServableProducts?.[paidSelfServableProducts.length - 1];

    // This is mostly here to make TypeScript happy. If we run into this in live, something has gone terribly wrong.
    if (!paidSelfServableProducts || !lowestProduct || !highestProduct) {
      throw new Error(
        'usePlanRecommendations(): Found no paid self-servable Observe products in the product catalog.',
      );
    }

    /**
     * If we already have maximum coverage, we recommend the next plan up from current products cc session.
     */

    if (coverageLevel === TrafficCoverageLevel.Max) {
      const currentAnalyticsSku = extractSkuFromPlanId(site.effectivePlanId, ProductType.Analytics);
      const currentAnalyticsProduct = getAnalyticsProductBySku(currentAnalyticsSku, productCatalog);
      return (
        paidSelfServableProducts?.find(
          (product) => product.dailyCcSessions > currentAnalyticsProduct.dailyCcSessions,
        ) ?? null
      );
    }

    // If site traffic is lower than our lowest plan's limit, the lowest plan will satisfy the needs.
    if (dailyTraffic <= lowestProduct.dailyCcSessions) {
      return lowestProduct;
    }

    /**
     * If the site traffic is higher than our highest plan's limit,
     * we provide no recommendation
     */
    if (dailyTraffic >= highestProduct.dailyCcSessions) {
      return null;
    }

    return (
      paidSelfServableProducts?.find((product) => product.dailyCcSessions >= dailyTraffic) ?? null
    );
  }, [
    isLoading,
    productCatalog,
    initialRecommendedPlanId,
    days,
    coverageLevel,
    dailyTraffic,
    defaultObserveProductIndex,
    effectiveObserveProductIndex,
    effectiveObserveProduct,
    site?.effectivePlanId,
  ]);

  /**
   * We currently don't have a solid way of coming up with a good Ask recommendation, so the best we can do for now
   * is recommending the site's current Ask plan. This mostly makes sense for trials, because this effectively
   * suggests users to subscribe to their trialed plans.
   */
  const feedbackProduct = useMemo(() => {
    if (isLoading || !productCatalog) {
      return null;
    }

    if (initialRecommendedPlanId) {
      const basedOnInitialRecommendedPlanId = getInitialRecommendedPlanId(
        initialRecommendedPlanId,
        ProductType.Feedback,
      );
      return getProductBySku(basedOnInitialRecommendedPlanId, productCatalog);
    }

    const currentFeedbackSku = extractSkuFromPlanId(site?.planId, ProductType.Feedback);
    const currentFeedbackProduct = getProductBySku(currentFeedbackSku, productCatalog);
    const defaultFeedbackProduct = getDefaultProductByTier(
      BUSINESS,
      ProductType.Feedback,
      productCatalog,
    );
    const paidSelfServableProducts = productCatalog?.feedbackProducts.filter(
      (product) => product.sku !== FREE_FEEDBACK_SKU && product.selfServeEnabled,
    );

    // This is mostly here to make TypeScript happy. If we run into this in live, something has gone terribly wrong.
    if (!paidSelfServableProducts) {
      throw new Error(
        'usePlanRecommendations(): Found no paid self-servable Ask products in the product catalog.',
      );
    }
    // Narrow down the product type with some type assertions
    assertIsFeedbackProduct(currentFeedbackProduct);
    assertIsFeedbackProduct(defaultFeedbackProduct);

    /**
     * If the trial is active or expired, and the site's current Ask product is a paid self-servable product,
     * we will recommend that.
     */
    if (
      (trialInfo?.state === TrialState.ACTIVE || trialInfo?.state === TrialState.EXPIRED) &&
      paidSelfServableProducts.some((product) => product.sku === currentFeedbackSku)
    ) {
      return currentFeedbackProduct;
    }

    /**
     * If the current sites Ask product is already higher or equal to the lowest Ask business plan,
     * we do not provide a recommendation
     */
    if (currentFeedbackProduct.monthlyResponses >= defaultFeedbackProduct.monthlyResponses) {
      return null;
    }

    // In any other case we recommend the lowest Ask Business.
    return getDefaultProductByTier(BUSINESS, ProductType.Feedback, productCatalog);
  }, [isLoading, productCatalog, site, trialInfo?.state, initialRecommendedPlanId]);

  const legacyPlanId =
    (analyticsProduct?.sku &&
      feedbackProduct?.sku &&
      getBundledPlanId({
        analyticsPlanId: analyticsProduct?.sku,
        feedbackPlanId: feedbackProduct?.sku,
      })) ??
    null;

  return {
    isLoading,
    legacyPlanId,
    products: {
      [ProductType.Analytics]: analyticsProduct,
      [ProductType.Feedback]: feedbackProduct,
    },
  };
};
